/**
 * DI Graph Generate Executor
 *
 * Per-project: statically analyzes the project's Inversify dependency DAG
 * (constructor injection from controllers — or library top-of-DAG classes —
 * down to leaves) and writes three checked-in files at the project root:
 *
 *   design.json — machine-readable graph (deterministic, sorted)
 *   design.md   — Mermaid diagram rendered by GitHub/IDEs in PRs
 *   design.html — clickable viz.js page (linked from architecture/dependencies.html)
 *
 * A project with NO design root writes NO files, and any stale ones are removed — see
 * writeDesignFiles for why an empty `{ "designs": [] }` is noise rather than information, and why
 * nothing downstream can tell "empty" from "absent".
 *
 * Runs on every build (cache:false; `ci` dependsOn this target directly).
 * Unrecognized DI patterns become "unresolved" nodes rather than failing the build.
 *
 * That the regenerated files are actually COMMITTED is no longer checked here (the deleted
 * validate-di-graph-unchanged target); it is one repo-wide "committed or staged" check in
 * `wp-review-upsert-pr` — @webpieces/pr-gate BuildArtifactGate.
 *
 * Config (webpieces.config.json, rule key `di-graph`): mode RUN_EVERY_TIME | OFF. That key still
 * governs THIS executor, so it stays in webpieces.config.json unchanged.
 *
 * Usage: nx run <project>:di-graph-generate
 */

import type { ExecutorContext } from '@nx/devkit';
import { loadAndValidate, ResolvedConfig } from '@webpieces/rules-config';
import * as fs from 'fs';
import * as path from 'path';
import {
    DiAnalyzer,
    frameworkTags,
    explicitRoleTag,
    FrameworkMarkers,
    selectAnalyzer,
} from '../../lib/di-graph/analyzer-strategy';
import { createProjectProgram } from '../../lib/di-graph/program';
import { toDesignJson } from '../../lib/di-graph/serializer';
import { toDesignMarkdown } from '../../lib/di-graph/mermaid';
import { generateDesignHTML } from '../../lib/di-graph/design-visualizer';
import { DiDesign, DiGraph } from '../../lib/di-graph/model';
import { toError } from '../../toError';

export interface DiGraphGenerateOptions {
    // No options here — config comes from webpieces.config.json at runtime.
}

export interface ExecutorResult {
    success: boolean;
}

const RULE_NAME = 'di-graph';
const MISSING_DESIGN_RULE_NAME = 'missing-design-annotation';

// Cheap substring pre-scan: a project whose source never mentions any DI marker
// gets an empty graph without paying for a ts.Program. Angular markers
// (@Component/bootstrapApplication) are included so an Angular app that uses no
// Inversify decorator isn't short-circuited to empty.
const DI_MARKERS = [
    '@DocumentDesign(',
    '@provideSingleton',
    '@provideFrameworkSingleton',
    '@provideTransient',
    '@injectable',
    'new ContainerModule',
    '@inject(',
    '@Component(',
    'bootstrapApplication',
];

const ANGULAR_MARKERS = ['@Component(', 'bootstrapApplication'];
// A non-Angular DI-design root. When no role tag is set, its presence steers the
// marker-fallback toward the Inversify analyzer (server/controller mode).
const DESIGN_ROOT_MARKER = '@DocumentDesign(';

/** Recursively read every project .ts file, folding each into `visit`. */
function forEachSourceFile(dir: string, visit: (content: string) => void): void {
    if (!fs.existsSync(dir)) return;
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
        const full = path.join(dir, entry.name);
        if (entry.isDirectory()) {
            if (entry.name === 'node_modules' || entry.name === 'dist') continue;
            forEachSourceFile(full, visit);
        } else if (entry.name.endsWith('.ts') && !entry.name.endsWith('.d.ts')) {
            visit(fs.readFileSync(full, 'utf-8'));
        }
    }
}

function sourceHasDiMarkers(dir: string): boolean {
    let found = false;
    forEachSourceFile(dir, (content: string) => {
        if (!found && DI_MARKERS.some((marker: string) => content.includes(marker))) found = true;
    });
    return found;
}

/** Pre-scan a project's source for the framework markers used when no tag is set. */
function detectFrameworkMarkers(dir: string): FrameworkMarkers {
    let angular = false;
    let controller = false;
    forEachSourceFile(dir, (content: string) => {
        if (!angular && ANGULAR_MARKERS.some((marker: string) => content.includes(marker))) angular = true;
        if (!controller && content.includes(DESIGN_ROOT_MARKER)) controller = true;
    });
    return new FrameworkMarkers(angular, controller);
}

/**
 * Repo-relative back link from a project's committed design.html up to
 * architecture/dependencies.html, so a reader who clicked a box in the
 * architecture graph can click back out. E.g. 'packages/http/http-api' →
 * '../../../architecture/dependencies.html'.
 */
function architectureBackHref(projectRoot: string): string {
    return path.posix.relative(projectRoot.replace(/\\/g, '/'), 'architecture/dependencies.html');
}

/** The three checked-in artifacts, named once so writing and reaping can never cover different sets. */
const DESIGN_FILES = ['design.json', 'design.md', 'design.html'];

/**
 * Delete this project's design artifacts, returning the names actually removed.
 *
 * Reaping rather than leaving them is the point: a project that USED to have a `@DocumentDesign` root
 * and lost it would otherwise keep serving a stale committed design that describes code no longer
 * there, which is worse than having none.
 */
// webpieces-disable no-function-outside-class -- nx executor module: nx resolves a default-export function here, and every helper in this file is module-scope by that contract
export function removeDesignFiles(projectRootAbs: string): string[] {
    const removed: string[] = [];
    for (const name of DESIGN_FILES) {
        const file = path.join(projectRootAbs, name);
        if (!fs.existsSync(file)) continue;
        fs.rmSync(file);
        removed.push(name);
    }
    return removed;
}

/**
 * Write the design artifacts — or, when there is NO design, make sure none exist.
 *
 * WHY AN EMPTY GRAPH WRITES NOTHING: `{ "designs": [] }` carries no information, and every project
 * without an Inversify/Angular DI root produced one — legacy Express services, plain libs, api-libs,
 * bundles. That is most of a monorepo carrying three committed files apiece that say nothing, showing
 * up in diffs and PR file lists forever.
 *
 * This is safe because nothing downstream distinguishes "empty file" from "no file", and that is by
 * design rather than luck: `graph-metadata.ts:hasGeneratedDesign()` documents that a MISSING or
 * unparseable design.json reads as "no design", so a box's node menu gets a "View Design" item only
 * when `designs[]` is non-empty either way. `wp-design-visualize` likewise only fails when ZERO
 * design.json exist repo-wide.
 *
 * The ONE-TIME cost is a commit deleting the empty files a repo already carries — surfaced by the
 * usual "build left the tree committed" gate, exactly like any other regenerated artifact.
 */
// webpieces-disable no-function-outside-class -- nx executor module: nx resolves a default-export function here, and every helper in this file is module-scope by that contract
export function writeDesignFiles(projectRootAbs: string, projectRoot: string, graph: DiGraph): void {
    if (graph.designs.length === 0) {
        const removed = removeDesignFiles(projectRootAbs);
        const detail = removed.length > 0 ? `removed stale ${removed.join(', ')}` : 'nothing to write or remove';
        console.log(`   No DI design for this project — ${detail}`);
        return;
    }
    // toDesignJson sorts the graph in place, so design.md/design.html below all
    // see the same deterministic ordering (no git churn on re-run).
    fs.writeFileSync(path.join(projectRootAbs, 'design.json'), toDesignJson(graph));
    fs.writeFileSync(path.join(projectRootAbs, 'design.md'), toDesignMarkdown(graph));
    fs.writeFileSync(
        path.join(projectRootAbs, 'design.html'),
        generateDesignHTML(graph, architectureBackHref(projectRoot)),
    );
}

/** The analyzer chosen for a project plus the role tag that drove the choice. */
class AnalyzerChoice {
    constructor(
        public readonly analyzer: DiAnalyzer,
        public readonly role: string | null,
    ) {}
}

/**
 * Select the analyzer by role (server & designed-lib → @DocumentDesign,
 * client→angular design, lib→skip). The explicit `role:` nx tag is the source of
 * truth; when absent we fall back to the legacy `framework:` selection + marker
 * pre-scan so designs stay identical until a project is retagged.
 */
function chooseAnalyzer(tags: string[], srcDir: string): AnalyzerChoice {
    const role = explicitRoleTag(tags);
    const frameworks = frameworkTags(tags);
    const analyzer = selectAnalyzer(role, frameworks, detectFrameworkMarkers(srcDir));
    console.log(
        `   Analyzer: ${analyzer.constructor.name} ` +
            `(role tag: ${role ?? 'none'}, framework tags: ${frameworks.length > 0 ? frameworks.join(', ') : 'none'})`,
    );
    return new AnalyzerChoice(analyzer, role);
}

/**
 * A server/designed-lib project that produced no design (zero @DocumentDesign
 * roots) fails the build with role-specific guidance. Enforced under the
 * `missing-design-annotation` rule.
 */
function reportMissingDesignAnnotation(projectName: string, role: string): void {
    if (role === 'server' || role === 'app') {
        console.error(
            `❌ ${projectName} is tagged role:${role} but has no @DocumentDesign class.\n` +
                `   A role:${role} project must expose one @DocumentDesign() root class (the App the ` +
                `container resolves via container.get(XxxApp)) so its design.json / design.html get\n` +
                `   generated and linked from architecture/dependencies.html.`,
        );
        return;
    }
    console.error(
        `❌ ${projectName} is tagged role:designed-lib but has no @DocumentDesign class.\n` +
            `   One or more classes you want a design printed for need @DocumentDesign() ` +
            `(from @webpieces/http-routing) added —\n` +
            `   or retag the project role:lib if it has no design.`,
    );
}

/**
 * A server/designed-lib project MUST expose at least one @DocumentDesign root,
 * else its design is empty and the role is meaningless. Returns true (and reports)
 * when the build should fail. The `missing-design-annotation` rule gates it:
 * absent (an older published config) → enforce; OFF → skip.
 */
function failsMissingDesignAnnotation(
    shared: ResolvedConfig,
    role: string | null,
    graph: DiGraph,
    projectName: string,
): boolean {
    const missingRule = shared.rules.get(MISSING_DESIGN_RULE_NAME);
    const enforce = !missingRule || !missingRule.isOff;
    if (enforce && graph.designs.length === 0 && (role === 'server' || role === 'app' || role === 'designed-lib')) {
        reportMissingDesignAnnotation(projectName, role);
        return true;
    }
    return false;
}

export default async function runExecutor(
    _options: DiGraphGenerateOptions,
    context: ExecutorContext,
): Promise<ExecutorResult> {
    const shared = loadAndValidate(context.root).resolved;
    const rule = shared.rules.get(RULE_NAME);
    if (rule && rule.isOff) {
        console.log(`\n⏭️  Skipping ${RULE_NAME} generation (mode: OFF)\n`);
        return { success: true };
    }

    const projectName = context.projectName ?? 'project';
    const projectConfig = context.projectsConfigurations?.projects[projectName];
    const projectRoot = projectConfig?.root ?? '.';
    const projectRootAbs = path.join(context.root, projectRoot);
    const srcDir = path.join(projectRootAbs, 'src');

    console.log(`\n🧬 Generating DI design graph for ${projectName}\n`);

    // eslint-disable-next-line @webpieces/no-unmanaged-exceptions -- chokepoint: a generator crash must produce an actionable failure, not a stack trace mid-build
    try {
        if (!sourceHasDiMarkers(srcDir)) {
            console.log('   No DI markers found');
            writeDesignFiles(projectRootAbs, projectRoot, new DiGraph(projectName));
            return { success: true };
        }

        const program = createProjectProgram(projectRootAbs);
        if (!program) {
            console.log('   No usable tsconfig/source');
            writeDesignFiles(projectRootAbs, projectRoot, new DiGraph(projectName));
            return { success: true };
        }

        const choice = chooseAnalyzer(projectConfig?.tags ?? [], srcDir);
        const graph = choice.analyzer.analyzeProject(program, context.root, projectRoot, projectName);

        if (failsMissingDesignAnnotation(shared, choice.role, graph, projectName)) {
            return { success: false };
        }

        writeDesignFiles(projectRootAbs, projectRoot, graph);

        if (graph.designs.length > 0) {
            const nodeCount = graph.designs.reduce((sum: number, d: DiDesign) => sum + d.nodes.length, 0);
            const edgeCount = graph.designs.reduce((sum: number, d: DiDesign) => sum + d.edges.length, 0);
            console.log(
                `✅ Wrote ${projectRoot}/design.json + design.md + design.html ` +
                    `(${graph.designs.length} design(s), ${nodeCount} node(s), ${edgeCount} edge(s))`,
            );
        }
        const unresolved = [...new Set(graph.designs.flatMap((d: DiDesign) => d.unresolved))];
        if (unresolved.length > 0) {
            console.warn(`⚠️  ${unresolved.length} unresolved token(s)/type(s): ${unresolved.join(', ')}`);
        }
        return { success: true };
    } catch (err: unknown) {
        const error = toError(err);
        console.error(`❌ DI graph generation failed for ${projectName}: ${error.message}`);
        console.error(`   To unblock builds, set rules["${RULE_NAME}"].mode="OFF" in webpieces.config.json.`);
        return { success: false };
    }
}
